if (!customElements.get('product-dtf')) {
  customElements.define(
    'product-dtf',
    class ProductDtf extends HTMLElement {
      connectedCallback() {
        const data = JSON.parse(this.querySelector('[data-dtf-data]').textContent);
        this.currency = data.currency;
        this.locale = data.locale;
        // Film size of each length, read from the variant title ("2 FT - 13 x 24 in": width x length).
        this.variants = data.variants.map((v) => ({ ...v, film: this.parseFilm(v.title) }));
        // Blank margin kept across the film width (none along the length): a 13 in film prints 12.5 in wide.
        this.widthMargin = 0.5;

        this.fileInput = this.querySelector('[data-dtf-file]');
        this.formatButtons = Array.from(this.querySelectorAll('[data-dtf-format]'));
        this.qtyInput = this.querySelector('[data-dtf-qty]');
        this.submitButton = this.querySelector('[data-dtf-submit]');

        this.file = null;
        this.design = null; // { width, height, dpi, assumedDpi } in inches
        const pressed = this.formatButtons.find((b) => b.getAttribute('aria-pressed') === 'true');
        this.variantId = Number(pressed?.dataset.variantId) || this.variants[0]?.id;

        this.fileInput.addEventListener('change', () => this.onFile(this.fileInput.files[0]));
        this.querySelector('[data-dtf-remove]').addEventListener('click', (evt) => {
          evt.preventDefault();
          evt.stopPropagation();
          this.onFile(null);
        });
        this.formatButtons.forEach((btn) =>
          btn.addEventListener('click', () => {
            this.variantId = Number(btn.dataset.variantId);
            this.render();
          })
        );
        this.querySelector('[data-dtf-qty-decrease]').addEventListener('click', () => this.setQty(this.qty() - 1));
        this.querySelector('[data-dtf-qty-increase]').addEventListener('click', () => this.setQty(this.qty() + 1));
        this.qtyInput.addEventListener('change', () => this.setQty(this.qty()));
        this.submitButton.addEventListener('click', () => this.addToCart());

        this.render();
      }

      /* ---------------------------------------------------------------- file */

      async onFile(file) {
        this.file = null;
        this.design = null;
        this.fileError = null;
        if (!file) {
          this.fileInput.value = '';
          this.render();
          return;
        }

        if (!this.fileType(file)) {
          this.fileError = 'This file type is not supported. Please upload a PNG or PDF file.';
        } else if (file.size > 20 * 1024 * 1024) {
          this.fileError =
            'This file is larger than 20 MB. Please export it as a PDF, or contact us at contact@ateliersoutheast.com to send it another way.';
        } else {
          this.file = file;
          try {
            this.design = await this.readDesignSize(file);
          } catch (error) {
            this.design = null;
          }
          if (!this.design) {
            this.fileError = "We couldn't read the size of this file. Please export it again as a PNG at its print size.";
            this.file = null;
          }
        }
        // Preselect the shortest length the design fits on (the customer can still pick a longer one).
        const best = this.bestVariant();
        if (best) this.variantId = best.id;
        if (!this.file) this.fileInput.value = '';
        this.render();
        if (this.file && !this.tooWide()) this.preUpload(this.file);
      }

      // The file is sent as soon as it is chosen, while the customer picks the length and quantity: Shopify takes
      // a few seconds per file, which made "Add to cart" slow. It goes up in a throwaway cart (no cookies: the
      // customer's cart is not touched), and "Add to cart" then sends its address instead of the file.
      preUpload(file) {
        const variantId = this.selectedVariant()?.id || this.variants[0]?.id;
        if (!variantId) return;
        const body = new FormData();
        body.append('items[0][id]', String(variantId));
        body.append('items[0][quantity]', '1');
        body.append('items[0][properties][_Upload]', file.name);
        body.append('items[0][properties][_Visual]', file);
        const job = fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          credentials: 'omit',
          headers: { Accept: 'application/json' },
          body,
        })
          .then((response) => response.json())
          .then((data) => {
            const url = data?.items?.[0]?.properties?._Visual;
            return typeof url === 'string' && /^https?:\/\//.test(url) ? url : null;
          })
          .catch(() => null); // the file itself is then sent with "Add to cart", as before
        this.upload = { file, job };
      }

      // Print size in inches. PNG/JPG: pixels / DPI (300 DPI when the file has none). PDF: page size.
      // SVG: width/height with their unit (or 96 px per inch).
      // Type from the browser, or from the extension when the browser doesn't give one.
      fileType(file) {
        const allowed = ['image/png', 'application/pdf'];
        if (allowed.includes(file.type)) return file.type;
        const extension = (file.name.split('.').pop() || '').toLowerCase();
        return { png: 'image/png', pdf: 'application/pdf' }[extension] || null;
      }

      async readDesignSize(file) {
        const type = this.fileType(file);
        if (type === 'image/png') return this.pngSize(new DataView(await file.slice(0, 1024 * 1024).arrayBuffer()));
        if (type === 'image/jpeg') return this.jpegSize(new DataView(await file.slice(0, 1024 * 1024).arrayBuffer()));
        if (type === 'application/pdf') return this.pdfSize(await file.text());
        if (type === 'image/svg+xml') return this.svgSize(await file.text());
        return null;
      }

      fromPixels(widthPx, heightPx, dpiX, dpiY) {
        const assumedDpi = !(dpiX > 0 && dpiY > 0);
        const x = assumedDpi ? 300 : dpiX;
        const y = assumedDpi ? 300 : dpiY;
        return { width: widthPx / x, height: heightPx / y, dpi: Math.round(Math.min(x, y)), assumedDpi, widthPx };
      }

      pngSize(view) {
        if (view.getUint32(0) !== 0x89504e47) return null;
        const width = view.getUint32(16);
        const height = view.getUint32(20);
        let dpiX = 0;
        let dpiY = 0;
        let offset = 8;
        while (offset + 8 <= view.byteLength) {
          const length = view.getUint32(offset);
          const type = String.fromCharCode(...[4, 5, 6, 7].map((i) => view.getUint8(offset + i)));
          if (type === 'pHYs' && offset + 17 <= view.byteLength && view.getUint8(offset + 16) === 1) {
            dpiX = view.getUint32(offset + 8) * 0.0254; // pixels per meter -> per inch
            dpiY = view.getUint32(offset + 12) * 0.0254;
          }
          if (type === 'IDAT' || type === 'IEND') break;
          offset += 12 + length;
        }
        return this.fromPixels(width, height, dpiX, dpiY);
      }

      jpegSize(view) {
        if (view.getUint16(0) !== 0xffd8) return null;
        let dpiX = 0;
        let dpiY = 0;
        let offset = 2;
        while (offset + 4 <= view.byteLength) {
          const marker = view.getUint16(offset);
          const length = view.getUint16(offset + 2);
          if (marker === 0xffe0 && view.getUint32(offset + 4) === 0x4a464946) {
            // JFIF: unit 1 = dots per inch, 2 = dots per cm
            const unit = view.getUint8(offset + 11);
            const x = view.getUint16(offset + 12);
            const y = view.getUint16(offset + 14);
            if (unit === 1) [dpiX, dpiY] = [x, y];
            if (unit === 2) [dpiX, dpiY] = [x * 2.54, y * 2.54];
          }
          if (marker >= 0xffc0 && marker <= 0xffcf && ![0xffc4, 0xffc8, 0xffcc].includes(marker)) {
            const height = view.getUint16(offset + 5);
            const width = view.getUint16(offset + 7);
            return this.fromPixels(width, height, dpiX, dpiY);
          }
          offset += 2 + length;
        }
        return null;
      }

      pdfSize(text) {
        // First page box, in points (72 per inch). TrimBox is the final size when the file has one.
        const box = text.match(/\/TrimBox\s*\[\s*([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s*\]/) ||
          text.match(/\/MediaBox\s*\[\s*([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s*\]/);
        if (!box) return null;
        // Large artboards (over 200 in, e.g. Illustrator) are saved smaller with a /UserUnit scale: a 22.8 × 47.2 in
        // sheet is a 2.28 × 4.72 in page with /UserUnit 10. The real size is the page size times that scale.
        const userUnit = Number(text.match(/\/UserUnit\s+([\d.]+)/)?.[1]) || 1;
        const [x0, y0, x1, y1] = box.slice(1).map(Number);
        return {
          width: (Math.abs(x1 - x0) / 72) * userUnit,
          height: (Math.abs(y1 - y0) / 72) * userUnit,
          dpi: null,
          assumedDpi: false,
        };
      }

      svgSize(text) {
        const svg = new DOMParser().parseFromString(text, 'image/svg+xml').querySelector('svg');
        if (!svg) return null;
        const perInch = { in: 1, cm: 2.54, mm: 25.4, pt: 72, pc: 6, px: 96, '': 96 };
        const toInches = (value) => {
          const match = /^\s*([\d.]+)\s*(in|cm|mm|pt|pc|px)?\s*$/.exec(value || '');
          return match ? Number(match[1]) / perInch[match[2] || ''] : null;
        };
        let width = toInches(svg.getAttribute('width'));
        let height = toInches(svg.getAttribute('height'));
        if (!width || !height) {
          const viewBox = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
          if (viewBox.length !== 4) return null;
          width = viewBox[2] / 96;
          height = viewBox[3] / 96;
        }
        return { width, height, dpi: null, assumedDpi: false };
      }

      /* -------------------------------------------------------------- format */

      parseFilm(title) {
        const match = /(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*(in|cm|mm)?/i.exec(title || '');
        if (!match) return null;
        const unit = (match[3] || 'in').toLowerCase();
        const divider = unit === 'cm' ? 2.54 : unit === 'mm' ? 25.4 : 1;
        return {
          width: parseFloat(match[1].replace(',', '.')) / divider,
          height: parseFloat(match[2].replace(',', '.')) / divider,
        };
      }

      // How the design goes on a film: 'upright', 'rotated' (turned a quarter to fit the width) or null (too
      // short, or too wide either way). The printable width is the film width minus the margin.
      placement(film) {
        if (!film || !this.design) return 'upright';
        const tolerance = 0.01;
        const printable = film.width - this.widthMargin + tolerance;
        const length = film.height + tolerance;
        const { width, height } = this.design;
        if (width <= printable && height <= length) return 'upright';
        if (height <= printable && width <= length) return 'rotated';
        return null;
      }

      fits(film) {
        return this.placement(film) !== null;
      }

      // Too wide for the film whichever way round: no length can help.
      tooWide() {
        if (!this.design) return false;
        const printable = Math.max(...this.variants.map((v) => v.film?.width || 0)) - this.widthMargin;
        return Math.min(this.design.width, this.design.height) > printable + 0.01;
      }

      // Shortest available length the design fits on.
      bestVariant() {
        if (!this.design) return null;
        return (
          this.variants
            .filter((v) => v.available && v.film && this.fits(v.film))
            .sort((a, b) => a.film.height - b.film.height)[0] || null
        );
      }

      selectedVariant() {
        return this.variants.find((v) => v.id === this.variantId) || null;
      }

      /* ------------------------------------------------------------ quantity */

      qty() {
        return Math.max(1, parseInt(String(this.qtyInput.value).replace(/\D/g, ''), 10) || 1);
      }

      setQty(value) {
        this.qtyInput.value = String(Math.max(1, value));
        this.render();
      }

      /* -------------------------------------------------------------- render */

      inches(value) {
        return `${Math.round(value * 10) / 10}`;
      }

      formatMoney(cents) {
        try {
          return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency }).format(cents / 100);
        } catch {
          return `$${(cents / 100).toFixed(2)}`;
        }
      }

      designLabel() {
        return this.design ? `${this.inches(this.design.width)} × ${this.inches(this.design.height)} in` : '';
      }

      // Design size saved on the order, with "rotated" when it is printed turned a quarter.
      orderDesignLabel() {
        const rotated = this.placement(this.selectedVariant()?.film) === 'rotated';
        return this.designLabel() + (rotated ? ' (rotated)' : '');
      }

      render() {
        const variant = this.selectedVariant();
        const qty = this.qty();

        // File display
        this.querySelector('[data-dtf-filename]').textContent = this.file ? this.file.name : '';
        this.querySelector('[data-dtf-remove]').hidden = !this.file;
        this.querySelector('[data-dtf-trigger]').hidden = Boolean(this.file);
        this.querySelector('[data-dtf-dropzone]').classList.toggle('has-error', Boolean(this.fileError));

        const detected = this.querySelector('[data-dtf-detected]');
        detected.hidden = !this.design;
        if (this.design) {
          detected.innerHTML = '';
          detected.append('Design size: ');
          const strong = document.createElement('strong');
          strong.textContent = this.designLabel();
          detected.append(strong);
        }

        // Length buttons: "Best fit" on the shortest one the design fits on, "Too short" on the shorter ones
        const best = this.bestVariant();
        this.formatButtons.forEach((btn) => {
          const v = this.variants.find((item) => item.id === Number(btn.dataset.variantId));
          const fits = this.fits(v?.film);
          btn.setAttribute('aria-pressed', v?.id === this.variantId ? 'true' : 'false');
          btn.classList.toggle('is-too-small', Boolean(this.design) && !fits);
          const fitNote = btn.querySelector('[data-dtf-fit]');
          const isBest = Boolean(best) && v?.id === best.id;
          fitNote.hidden = !this.design || this.tooWide() || (fits && !isBest);
          fitNote.textContent = isBest ? 'Best fit' : 'Too short';
        });

        // Messages: file error, design larger than the chosen film, low resolution
        let message = this.fileError;
        let isError = Boolean(this.fileError);
        let isInfo = false;
        const fitsSelected = this.fits(variant?.film);
        const printableWidth = this.inches((variant?.film?.width || 13) - this.widthMargin);
        if (!message && this.design && this.tooWide()) {
          message = `Your design (${this.designLabel()}) is too wide: the printable width of our film is ${printableWidth} in. Please resize your file.`;
          isError = true;
        } else if (!message && this.design && !fitsSelected) {
          message =
            `Your design (${this.designLabel()}) is longer than ${variant.title.split(' - ')[0]}. ` +
            (best
              ? `Choose ${best.title.split(' - ')[0]} or longer.`
              : 'It is longer than our longest film: please resize your file.');
          isError = true;
        } else if (!message && this.design && this.placement(variant?.film) === 'rotated') {
          message = `Your design will be rotated to fit the ${printableWidth} in printable width of the film.`;
          isInfo = true;
        } else if (!message && this.design?.dpi && this.design.dpi < 150 && !this.design.assumedDpi) {
          const sharpWidth = Math.ceil(this.design.width * 300);
          message = `Your image is ${this.design.widthPx} px wide: it may look blurry once printed. For a sharp print, use an image at least ${sharpWidth} px wide.`;
        }
        const messageEl = this.querySelector('[data-dtf-message]');
        messageEl.hidden = !message;
        messageEl.classList.toggle('is-low', isError);
        messageEl.classList.toggle('is-info', isInfo);
        this.querySelector('[data-dtf-message-text]').textContent = message || '';

        // Price
        const unit = variant?.price || 0;
        this.querySelector('[data-dtf-unit]').textContent = variant ? this.formatMoney(unit) : '—';
        this.querySelector('[data-dtf-total]').textContent = variant ? this.formatMoney(unit * qty) : '—';

        // Add to cart
        const notice = this.querySelector('[data-dtf-notice]');
        let issue = null;
        if (!this.file || !this.design) issue = 'Please upload your file to continue.';
        else if (this.tooWide()) issue = 'Please upload a design that fits the film width.';
        else if (!fitsSelected) issue = 'Please choose a length your design fits on.';
        else if (!variant?.available) issue = 'This format is sold out.';
        notice.hidden = !issue;
        notice.textContent = issue || '';
        this.submitButton.disabled = Boolean(issue) || this.submitting;
      }

      /* --------------------------------------------------------- add to cart */

      async addToCart() {
        if (this.submitButton.disabled) return;
        const variant = this.selectedVariant();
        this.submitting = true;
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');
        this.showError(null);

        const formData = new FormData();
        formData.append('items[0][id]', String(variant.id));
        formData.append('items[0][quantity]', String(this.qty()));
        formData.append('items[0][properties][Design size]', this.orderDesignLabel());
        // "File": the file name, shown to the customer at checkout and on the order status page.
        formData.append('items[0][properties][File]', this.file.name);
        formData.append('items[0][properties][_Design file]', this.file.name);
        // Already sent in the background: its address; otherwise the file itself.
        const uploadedUrl = this.upload && this.upload.file === this.file ? await this.upload.job : null;
        formData.append('items[0][properties][_Visual]', uploadedUrl || this.file);

        try {
          const response = await fetch(this.dataset.cartAddUrl, {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          const json = await response.json().catch(() => ({}));
          if (!response.ok || json.status) throw new Error(json.description || json.message || 'Could not add to cart.');
          window.location = this.dataset.cartUrl;
        } catch (error) {
          this.showError(error.message);
          this.submitting = false;
          this.submitButton.classList.remove('loading');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
          this.render();
        }
      }

      showError(message) {
        const wrapper = this.querySelector('.product-form__error-message-wrapper');
        wrapper.hidden = !message;
        this.querySelector('[data-dtf-error]').textContent = message || '';
      }
    }
  );
}
